import type { QueryClient } from '@tanstack/react-query';
import type { ProjectTask } from '@/api/client';

export type ProjectTaskChangedPayload =
  | { projectId: string; task: ProjectTask }
  | { projectId: string; task_id: string; deleted: true };

export const projectTasksQueryKey = (projectId: string) => ['projectTasks', projectId] as const;

/** Порядок сервера (спек «Строка задачи»): открытые — новые сверху, выполненные — последние выполненные сверху. */
export function sortTasks(tasks: ProjectTask[]): ProjectTask[] {
  return [...tasks].sort((a, b) => {
    const ad = a.done_at !== null, bd = b.done_at !== null;
    if (ad !== bd) return ad ? 1 : -1;
    return ad ? b.done_at!.localeCompare(a.done_at!) : b.created_at.localeCompare(a.created_at);
  });
}

export function upsertTask(list: ProjectTask[] | undefined, task: ProjectTask): ProjectTask[] {
  const prev = list ?? [];
  const i = prev.findIndex((x) => x.task_id === task.task_id);
  if (i === -1) return sortTasks([...prev, task]);
  // Не откатывать свежую версию запоздалым событием
  if (prev[i].updated_at > task.updated_at) return prev;
  const next = [...prev];
  next[i] = task;
  return sortTasks(next);
}

/**
 * Обновить только существующую задачу: ответ на запрос, отправленный до удаления, не должен её воскрешать.
 * Свежую версию (realtime) запоздалым ответом не откатываем — как в upsertTask.
 */
export function updateTask(list: ProjectTask[] | undefined, task: ProjectTask): ProjectTask[] | undefined {
  const i = list?.findIndex((x) => x.task_id === task.task_id) ?? -1;
  if (!list || i === -1 || list[i].updated_at > task.updated_at) return list;
  const next = [...list];
  next[i] = task;
  return sortTasks(next);
}

export function removeTask(list: ProjectTask[] | undefined, taskId: string): ProjectTask[] {
  const prev = list ?? [];
  return prev.some((x) => x.task_id === taskId) ? prev.filter((x) => x.task_id !== taskId) : prev;
}

/** Точечная правка открытого списка; закрытый список не создаётся — он загрузится при открытии. */
export function applyTaskChanged(qc: QueryClient, payload: ProjectTaskChangedPayload): void {
  const key = projectTasksQueryKey(payload.projectId);
  if (qc.getQueryData(key) === undefined) return;
  qc.setQueryData<ProjectTask[]>(key, (prev) =>
    'deleted' in payload ? removeTask(prev, payload.task_id) : upsertTask(prev, payload.task)
  );
}
